import React, { Component } from "react";
import { Dropdown, Checkbox } from "semantic-ui-react";
import { AxiosResponse } from "axios";
import ReactHtmlParser from "react-html-parser";
import NumberFormat from "react-number-format";
import _ from "lodash";
import { getRequest, postRequest } from "../../../utils/httpRequest";
import { notification } from "../../../utils/helpers";
import { Props, State, LeaderboardSetup, Hero, Response } from "./Types";
import TeamMember from "./TeamMember";
import MakeAdminsModal from "./modals/MakeAdminsModal";
import DeleteUsersModal from "./modals/DeleteUsersModal";

class Leaderboard extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      courses: [],
      heroes: [],
      selectedCourse: "",
      count: 20,
      query: "",
      page: 0,
      totalPages: 0,
      allMembersSelected: false,
      loading: false,
      showDeleteUsersModal: false,
      showMakeAdminsModal: false,
      allocatableCourses: [],
      allocateOpen: false,
      selectedAllocations: [],
      showAllocateConfirm: false,
      allocating: false,
      courseFilterOpen: false,
      submitting: false,
    };
  }

  courseFilterRef = React.createRef<HTMLDivElement>();
  allocateRef = React.createRef<HTMLDivElement>();

  componentDidMount() {
    this.setState({ loading: true });

    document.addEventListener("mousedown", this.handleOutsidePointer);
    document.addEventListener("touchstart", this.handleOutsidePointer);
    document.addEventListener("keydown", this.handleFilterKeyDown);

    const { slug } = this.props;

    // Only the courses this team can actually be enrolled into, so the picker
    // can't offer something the allocation would then refuse.
    getRequest(`/api/teams/${slug}/allocatable_courses.json`)
      .then((response: AxiosResponse<{ courses: any[] }>) =>
        this.setState({ allocatableCourses: response.data.courses }),
      )
      .catch(() => this.setState({ allocatableCourses: [] }));

    return getRequest("/api/profile/leaderboard_setup.json").then(
      (response: AxiosResponse<LeaderboardSetup>) => {
        const { data } = response;
        const courses = [
          { text: "All Courses", value: "all", key: "all" },
        ].concat(
          data.courses.map((course) => ({
            text: course.name,
            value: course.slug,
            key: course.slug,
          })),
        );

        this.setState({
          courses,
          hasCurrentMemberShip: data.has_current_membership,
          loading: false,
        });
      },
    );
  }

  handleKeyDown = (e) => {
    if (e.key === "Enter") {
      this.getHeroes(e, this.state.count);
    }
  };

  getHeroes = (e, count, newPage = 0) => {
    e.preventDefault();

    this.setState({ loading: true, count });
    const { selectedCourse, query } = this.state;

    return getRequest(
      `/api/teams/${this.props.slug}/leaderboard_search.json?course_slug=${
        selectedCourse === "all" ? "" : selectedCourse
      }&query=${query}&count=${count}&page=${newPage + 1}`,
    ).then((response: AxiosResponse<Response>) => {
      const { data } = response;

      let heroes = data.heroes.map((hero) => {
        return { ...hero, selected: false };
      });

      this.props.updateHeroes(heroes);
      this.setState({
        loading: false,
        page: newPage,
        totalPages: data.total_pages,
      });
    });
  };

  loadPrev = (event) => {
    event.preventDefault();
    const { page, count } = this.state;
    const newPage = this.prevPage(page);

    this.getHeroes(event, count, newPage);
  };

  loadNext = (event) => {
    event.preventDefault();
    const { page, count, totalPages } = this.state;
    const nextPageNum = this.nextPage(page, totalPages);
    const newPage = nextPageNum === totalPages ? totalPages - 1 : nextPageNum;

    this.getHeroes(event, count, newPage);
  };

  prevPage = (page) => {
    if (page < 1) {
      return 0;
    } else if (page + 1 === 1) {
      return 1;
    } else {
      return page - 1;
    }
  };

  nextPage = (page, totalPages) => (page > totalPages ? totalPages : page + 1);

  handleInputChange = (e) => {
    this.setState({ query: e.target.value });
  };

  // Untick the last person and the button greys out again, so the picker must
  // not be left hanging open over a selection that no longer exists.
  componentDidUpdate(prevProps: Props) {
    const wasSelected = prevProps.heroes.filter((hero) => hero.selected).length;
    const nowSelected = this.props.heroes.filter((hero) => hero.selected).length;

    if (wasSelected > 0 && nowSelected === 0 && this.state.allocateOpen) {
      this.setState({ allocateOpen: false });
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleOutsidePointer);
    document.removeEventListener("touchstart", this.handleOutsidePointer);
    document.removeEventListener("keydown", this.handleFilterKeyDown);
  }

  handleOutsidePointer = (event: MouseEvent | TouchEvent) => {
    const { courseFilterOpen, allocateOpen } = this.state;

    if (
      courseFilterOpen &&
      !this.courseFilterRef.current?.contains(event.target as Node)
    ) {
      this.setState({ courseFilterOpen: false });
    }

    if (
      allocateOpen &&
      !this.allocateRef.current?.contains(event.target as Node)
    ) {
      this.setState({ allocateOpen: false });
    }
  };

  handleFilterKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape") return;

    if (this.state.showAllocateConfirm) {
      this.setState({ showAllocateConfirm: false });
      return;
    }

    if (this.state.courseFilterOpen) this.setState({ courseFilterOpen: false });
    if (this.state.allocateOpen) this.setState({ allocateOpen: false });
  };

  toggleCourseFilter = () =>
    this.setState((current) => ({ courseFilterOpen: !current.courseFilterOpen }));

  selectCourse = (event: React.MouseEvent, course) => {
    event.preventDefault();
    this.setState({ courseFilterOpen: false });
    // getHeroes only wants something with preventDefault on it, and React 16
    // has recycled the real event by the time the setState callback runs.
    this.handleChange(
      "selectedCourse",
      { preventDefault: () => undefined },
      { value: course.value },
    );
  };

  handleChange(key, event, data) {
    this.setState({ [key]: data.value }, () =>
      this.getHeroes(event, this.state.count),
    );
  }

  handleAllMembersSelected = (e, data) => {
    let { heroes, updateHeroes } = this.props;

    this.setState({ allMembersSelected: data.checked });

    const updatedHeroes = heroes.map((hero) => {
      return { ...hero, selected: data.checked };
    });

    updateHeroes(updatedHeroes);
  };

  handleSelectMember = (e, value, idx, key) => {
    let heroes = [...this.props.heroes];
    heroes[idx] = {
      ...heroes[idx],
      [key]: value,
    };
    this.props.updateHeroes(heroes);
  };

  handleShowDeleteUsersModal = (e) => {
    e.preventDefault();

    const users = this.getSelectedUsers();

    if (users.length === 0) {
      alert("Please select users!");
    } else {
      this.setState({ showDeleteUsersModal: true });
    }
  };

  handleShowMakeAdminsModal = (e) => {
    e.preventDefault();

    const users = this.getSelectedUsers();

    if (users.length === 0) {
      alert("Please select users!");
    } else {
      this.setState({ showMakeAdminsModal: true });
    }
  };

  toggleAllocatePicker = () =>
    this.setState((current) => ({ allocateOpen: !current.allocateOpen }));

  toggleAllocatedCourse = (slug: string) =>
    this.setState((current) => ({
      selectedAllocations: current.selectedAllocations.includes(slug)
        ? current.selectedAllocations.filter((s) => s !== slug)
        : current.selectedAllocations.concat(slug),
    }));

  openAllocateConfirm = () =>
    this.setState({ allocateOpen: false, showAllocateConfirm: true });

  // Worked out before the admin confirms, not after, and from data the rows
  // already carry -- so the dialog can name who is already on what rather than
  // reporting it once the emails have gone.
  allocationPlan = () => {
    const { heroes } = this.props;
    const { allocatableCourses, selectedAllocations } = this.state;

    const people = heroes.filter((hero) => hero.selected);
    const chosen = allocatableCourses.filter((course) =>
      selectedAllocations.includes(course.slug),
    );

    // Grouped by learner rather than one line per person-and-course: three
    // people against three courses is nine lines otherwise, most of them
    // repeating the same name.
    const alreadyOn = [];

    people.forEach((person) => {
      const clashes = chosen.filter((course) =>
        (person.enrolments || []).some(
          (enrolment) => enrolment.course_slug === course.slug,
        ),
      );

      if (clashes.length > 0) {
        alreadyOn.push({
          key: String(person.id),
          name: person.protected_name,
          courses: clashes.map((course) => course.name),
        });
      }
    });

    const clashCount = alreadyOn.reduce(
      (total, entry) => total + entry.courses.length,
      0,
    );

    return {
      people,
      chosen,
      alreadyOn,
      newCount: people.length * chosen.length - clashCount,
    };
  };

  closeAllocateConfirm = () => this.setState({ showAllocateConfirm: false });

  allocateCourses = () => {
    const { slug } = this.props;
    const { selectedAllocations } = this.state;
    const users = this.getSelectedUsers();

    this.setState({ allocating: true });

    return postRequest(`/api/teams/${slug}/allocate_courses.json`, {
      user_ids: users,
      course_ids: selectedAllocations,
      organization_id: slug,
    })
      .then(() => {
        this.setState({
          allocating: false,
          showAllocateConfirm: false,
          selectedAllocations: [],
        });
        notification(
          `${selectedAllocations.length === 1 ? "Course" : "Courses"} allocated. ` +
            "Welcome emails are on their way.",
          "notification",
        );
      })
      .catch(() => {
        this.setState({ allocating: false, showAllocateConfirm: false });
        notification(
          "We couldn't allocate those courses. Please try again.",
          "error",
        );
      });
  };

  handleHideDeleteUsersModal = (e) => {
    e.preventDefault();

    this.setState({ showDeleteUsersModal: false });
  };

  handleHideMakeAdminsModal = (e) => {
    e.preventDefault();

    this.setState({ showMakeAdminsModal: false });
  };

  getSelectedUsers = () => {
    const { heroes } = this.props;

    let selectedUsers = _.filter(heroes, ["selected", true]);

    return _.map(selectedUsers, "id");
  };

  deleteUsers = (e) => {
    e.preventDefault();

    const { slug } = this.props;
    this.setState({ submitting: true });

    const users = this.getSelectedUsers();

    const params = {
      situation: "remove_user",
      user_ids: users,
    };

    postRequest(`/api/teams/${slug}/manage_members.json`, params)
      .then(() => {
        this.setState({ submitting: false, showDeleteUsersModal: false });
        notification("Users removed successfully", "notification");
        this.getHeroes(e, this.state.count);
      })
      .catch(() => {
        this.setState({ submitting: false });
        notification("Error removing users", "error");
      });
  };

  makeAdmins = (e) => {
    e.preventDefault();

    const { slug } = this.props;
    this.setState({ submitting: true });

    const users = this.getSelectedUsers();

    const params = {
      situation: "manage_admin",
      user_ids: users,
    };

    postRequest(`/api/teams/${slug}/manage_members.json`, params)
      .then(() => {
        this.setState({ submitting: false, showMakeAdminsModal: false });
        notification("Users updated successfully", "notification");
        this.getHeroes(e, this.state.count);
      })
      .catch(() => {
        this.setState({ submitting: false });
        notification("Error updating users", "error");
      });
  };

  render() {
    const { heroes, slug } = this.props;

    const {
      query,
      courses,
      selectedCourse,
      count,
      page,
      allMembersSelected,
      loading,
      showDeleteUsersModal,
      showMakeAdminsModal,
      allocatableCourses,
      allocateOpen,
      selectedAllocations,
      showAllocateConfirm,
      allocating,
      courseFilterOpen,
      submitting,
      totalPages,
    } = this.state;

    const currentCourse = courses.filter(
      (courses) => courses.value === selectedCourse,
    )[0];

    const selectedCount = heroes.filter((hero) => hero.selected).length;
    const plan = this.allocationPlan();

    return (
      <section className="lb-section" id="team-leaderboard">
        <div className="container">
          <div className="lb-head">
            <div>
              <h2 className="section-heading">Leaderboard</h2>
              <p className="section-sub">
                Everyone on the team, ranked by XP. Open a row to see course
                progress and who's an admin.
              </p>
            </div>
          </div>

          {/* Toolbar */}
          <div className="lb-toolbar">
            <div className="search">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3-3" />
              </svg>
              <label className="sr-only" htmlFor="lbSearch">
                Search team members by name
              </label>
              <input
                type="search"
                id="lbSearch"
                placeholder="Search by name..."
                value={query}
                onChange={this.handleInputChange}
                onKeyDown={this.handleKeyDown}
              />
            </div>

            {/* The brand's pill dropdown, as on the courses page, rather than
                the browser's own select control. */}
            <div className="custom-select" ref={this.courseFilterRef}>
              <span className="sr-only" id="courseFilterLabel">
                Filter by course
              </span>
              <button
                type="button"
                className="cs-trigger"
                aria-haspopup="listbox"
                aria-expanded={courseFilterOpen}
                aria-labelledby="courseFilterLabel courseFilterText"
                aria-controls="courseFilterMenu"
                onClick={this.toggleCourseFilter}
              >
                <span className="cs-trigger-text" id="courseFilterText">
                  {(currentCourse || courses[0])?.text || "All Courses"}
                </span>
                <span className="cs-chevron" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </button>

              <ul
                className="cs-menu"
                id="courseFilterMenu"
                role="listbox"
                aria-labelledby="courseFilterLabel"
                tabIndex={-1}
                hidden={!courseFilterOpen}
              >
                {courses.map((course) => (
                  <li
                    key={course.key}
                    role="option"
                    aria-selected={
                      (currentCourse || courses[0])?.value === course.value
                    }
                    onClick={(event) => this.selectCourse(event, course)}
                  >
                    {course.text}
                  </li>
                ))}
              </ul>
            </div>

            <div className="toolbar-spacer" />

            {/* Greyed out until somebody is ticked, since there is nobody to
                allocate to until then. The count in the label is the reminder
                of who it is about to affect. */}
            <div className="allocate-wrap" ref={this.allocateRef}>
              <button
                className="btn btn-secondary btn--sm"
                type="button"
                disabled={selectedCount === 0}
                aria-haspopup="true"
                aria-expanded={allocateOpen}
                aria-controls="allocateMenu"
                title={
                  selectedCount === 0
                    ? "Select team members first"
                    : undefined
                }
                onClick={this.toggleAllocatePicker}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
                </svg>
                Allocate courses
                {selectedCount > 0 && ` (${selectedCount})`}
              </button>

              <div
                className="allocate-menu"
                id="allocateMenu"
                hidden={!allocateOpen}
              >
                <div className="allocate-menu__head">
                  Courses to allocate to{" "}
                  <b>
                    {selectedCount}{" "}
                    {selectedCount === 1 ? "person" : "people"}
                  </b>
                </div>

                <div className="allocate-menu__list">
                  {allocatableCourses.length === 0 && (
                    <p className="allocate-menu__empty">
                      No courses are available to allocate.
                    </p>
                  )}
                  {allocatableCourses.map((course) => (
                    <label className="allocate-opt" key={course.slug}>
                      <input
                        type="checkbox"
                        checked={selectedAllocations.includes(course.slug)}
                        onChange={() => this.toggleAllocatedCourse(course.slug)}
                      />
                      <span>{course.name}</span>
                    </label>
                  ))}
                </div>

                <div className="allocate-menu__foot">
                  <button
                    className="btn btn-primary btn--sm btn--block"
                    type="button"
                    disabled={selectedAllocations.length === 0}
                    onClick={this.openAllocateConfirm}
                  >
                    Allocate these courses
                    {selectedAllocations.length > 0 &&
                      ` (${selectedAllocations.length})`}
                  </button>
                </div>
              </div>
            </div>

            {/* The bar chart said "here are some statistics"; the button hands
                you a CSV. The download arrow says what actually happens. */}
            <a
              className="btn btn-secondary btn--sm"
              href={`/teams/${slug}/download_usage.csv`}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3v12M7 10l5 5 5-5M4 21h16" />
              </svg>
              Usage data
            </a>

            <div className="showcount" role="group" aria-label="Rows per page">
              <span>Show</span>
              {[20, 50, 100].map((option) => (
                <button
                  className={`opt ${count === option ? "is-on" : ""}`}
                  type="button"
                  key={option}
                  aria-pressed={count === option}
                  onClick={(event) => this.getHeroes(event, option)}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          {/* Only appears once something is ticked, so the destructive actions
              aren't sitting there permanently inviting a stray click. */}
          {selectedCount > 0 && (
            <div className="action-bar" role="region" aria-label="Actions for selected members">
              <span className="count">
                <em>{selectedCount}</em> selected
              </span>
              <button className="act" type="button" onClick={this.handleShowMakeAdminsModal}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 3 4 6v5c0 4.5 3.2 7.5 8 9 4.8-1.5 8-4.5 8-9V6l-8-3Z" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
                Make admin
              </button>
              <button className="act act-danger" type="button" onClick={this.handleShowDeleteUsersModal}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />
                </svg>
                Remove from team
              </button>
            </div>
          )}

          <div className="lb-card">
            {loading && (
              <div className="lb-loading">
                <div className="ui active centered inline loader" />
              </div>
            )}

            <table className="lb">
              <caption className="sr-only">
                Team leaderboard. Select members to manage them, or open a row
                for course detail.
              </caption>
              <thead>
                <tr>
                  <th className="col-check" scope="col">
                    <label className="sr-only" htmlFor="selAll">
                      Select all members
                    </label>
                    <input
                      type="checkbox"
                      className="cb"
                      id="selAll"
                      checked={allMembersSelected}
                      onChange={(event) =>
                        this.handleAllMembersSelected(event, {
                          checked: event.target.checked,
                        })
                      }
                    />
                  </th>
                  <th className="col-rank" scope="col">Rank</th>
                  <th className="col-name" scope="col">Name</th>
                  <th className="col-courses" scope="col">Courses</th>
                  <th className="col-points" scope="col">Points</th>
                  <th className="col-activity" scope="col">Last activity</th>
                  <th className="col-expand" scope="col">
                    <span className="sr-only">Expand row</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {heroes.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="lb-empty">
                        <h3>No one to show</h3>
                        <p>
                          No team members match that search. Try a different
                          name, or clear the course filter.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  heroes.map((hero, i) => (
                    <TeamMember
                      hero={hero}
                      key={hero.id}
                      idx={i}
                      page={page}
                      count={count}
                      slug={slug}
                      handleSelectMember={this.handleSelectMember}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <nav className="pager" aria-label="Leaderboard pages">
              <button
                className="page-chevron"
                type="button"
                aria-label="Previous page"
                disabled={page === 0}
                onClick={this.loadPrev}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <span className="pager-status">
                Page {page + 1} of {totalPages}
              </span>
              <button
                className="page-chevron"
                type="button"
                aria-label="Next page"
                disabled={page + 1 >= totalPages}
                onClick={this.loadNext}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </nav>
          )}
        </div>
        {/* Allocating enrols people and emails them, so it asks first --
            spelling out both halves, because the email is the part an admin
            can't take back. */}
        <div className="team-manage-modal">
          <div
            className={`backdrop ${showAllocateConfirm ? "is-open" : ""}`}
            aria-hidden={!showAllocateConfirm}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                this.closeAllocateConfirm();
              }
            }}
          >
            <div
              className="dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="allocateConfirmTitle"
            >
              <h2 id="allocateConfirmTitle">Allocate these courses?</h2>
              <p className="dialog-intro">
                {plan.newCount > 0 ? (
                  <>
                    <b>
                      {plan.newCount}{" "}
                      {plan.newCount === 1 ? "enrolment" : "enrolments"}
                    </b>{" "}
                    across {selectedCount}{" "}
                    {selectedCount === 1 ? "person" : "people"}. Everyone newly
                    enrolled gets an email welcoming them to the course, the
                    same one they'd get on joining it themselves.
                  </>
                ) : (
                  <>
                    Nothing to do:{" "}
                    <b>
                      everyone selected is already on{" "}
                      {plan.chosen.length === 1
                        ? "that course"
                        : "those courses"}
                    </b>
                    . No enrolments would change and no emails would be sent.
                  </>
                )}
              </p>

              <ul className="allocate-summary">
                {plan.chosen.map((course) => (
                  <li key={course.slug}>{course.name}</li>
                ))}
              </ul>

              {plan.alreadyOn.length > 0 && (
                <div className="allocate-clash">
                  <ul>
                    {plan.alreadyOn.map((clash) => (
                      <li key={clash.key}>
                        <b>{clash.name}</b> is already enrolled in{" "}
                        {clash.courses.map((course, index) => (
                          <React.Fragment key={course}>
                            {index > 0 &&
                              (index === clash.courses.length - 1
                                ? " and "
                                : ", ")}
                            <b>{course}</b>
                          </React.Fragment>
                        ))}
                        .
                      </li>
                    ))}
                  </ul>
                  <p>
                    {plan.newCount > 0
                      ? "The above learners are already enrolled. All the other allocations will be sent."
                      : "The above learners are already enrolled. Nothing will be sent."}
                  </p>
                </div>
              )}

              <p className="nf-hint">
                Anyone without an active subscription is skipped.
              </p>

              <div className="dialog-foot">
                <button
                  className="btn btn-ghost btn--sm"
                  type="button"
                  onClick={this.closeAllocateConfirm}
                >
                  {plan.newCount > 0 ? "Cancel" : "Close"}
                </button>
                {plan.newCount > 0 && (
                  <button
                    className="btn btn-primary btn--sm"
                    type="button"
                    disabled={allocating}
                    onClick={this.allocateCourses}
                  >
                    {allocating ? "Allocating..." : "Yes, allocate"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <MakeAdminsModal
          showMakeAdminsModal={showMakeAdminsModal}
          handleHideMakeAdminsModal={this.handleHideMakeAdminsModal}
          makeAdmins={this.makeAdmins}
          submitting={submitting}
        />
        <DeleteUsersModal
          showDeleteUsersModal={showDeleteUsersModal}
          handleHideDeleteUsersModal={this.handleHideDeleteUsersModal}
          deleteUsers={this.deleteUsers}
          submitting={submitting}
        />
      </section>
    );
  }
}

export default Leaderboard;
